import { useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import localeText from "../../../../locale";
import FormField from "./FormField";

const MAX_MB = 5;

// Imagem com recorte obrigatório numa proporção fixa (ex.: logo 4:1, favicon 1:1).
// `currentUrl` é a imagem já salva; `file` é o recorte novo, ainda não enviado.
const CroppedImageField = ({
  label,
  hint,
  ratio,
  ratioLabel,
  currentUrl,
  file,
  onChange,
  onError,
  previewClassName,
  disabled,
}) => {
  const inputRef = useRef(null);
  const cropperRef = useRef(null);
  const [source, setSource] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const pick = (selected) => {
    if (!selected) return;
    if (!selected.type.startsWith("image/")) {
      onError(localeText("adminFormImageInvalid"));
      return;
    }
    if (selected.size / 1024 / 1024 > MAX_MB) {
      onError(localeText("fileSizeTooBig"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setSource(reader.result);
    reader.readAsDataURL(selected);
  };

  const applyCrop = () => {
    const canvas = cropperRef.current?.cropper?.getCroppedCanvas();
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      onChange(new File([blob], "image.png", { type: "image/png" }));
      setSource(null);
    }, "image/png");
  };

  const shown = preview || currentUrl;

  return (
    <FormField label={`${label} · ${ratioLabel}`} hint={hint}>
      <div
        className="admin-image-picker"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          if (!disabled) pick(e.dataTransfer.files?.[0]);
        }}
      >
        <div className={`admin-cropped-image__preview ${previewClassName || ""}`}>
          {shown ? <img src={shown} alt="" /> : <i className="pi pi-image" aria-hidden="true" />}
          {file && <span className="admin-banner-slot__new">{localeText("adminBannersNew")}</span>}
        </div>
        <div className="admin-image-picker__actions">
          <button
            type="button"
            className="admin-btn"
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
          >
            <i className="pi pi-upload" aria-hidden="true" />
            {localeText(shown ? "adminFormImageReplace" : "adminFormImageChoose")}
          </button>
          {file && (
            <button type="button" className="admin-link-btn" onClick={() => onChange(null)}>
              {localeText(currentUrl ? "adminFormImageKeepCurrent" : "adminFormImageRemove")}
            </button>
          )}
          <span className="admin-muted">{localeText("adminFormImageDrop")}</span>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            pick(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
      </div>

      <Dialog
        visible={!!source}
        onHide={() => setSource(null)}
        header={
          <div>
            <div className="admin-dialog__title">{localeText("cropImage")}</div>
            <div className="admin-dialog__subtitle">{`${label} · ${ratioLabel}`}</div>
          </div>
        }
        draggable={false}
        resizable={false}
        className="admin-dialog"
        style={{ width: "44rem" }}
        breakpoints={{ "760px": "calc(100vw - 1rem)" }}
        footer={
          <div className="admin-confirm__footer">
            <button type="button" className="admin-btn" onClick={() => setSource(null)}>
              {localeText("cancel")}
            </button>
            <button type="button" className="admin-btn admin-btn--primary" onClick={applyCrop}>
              <i className="pi pi-check" aria-hidden="true" />
              {localeText("cropImage")}
            </button>
          </div>
        }
      >
        {source && (
          <Cropper
            ref={cropperRef}
            src={source}
            aspectRatio={ratio}
            viewMode={1}
            autoCropArea={1}
            guides
            background={false}
            style={{ height: 340, width: "100%" }}
          />
        )}
      </Dialog>
    </FormField>
  );
};

export default CroppedImageField;
